import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, History } from "lucide-react";
import { Badge } from "../../Franchise/D2CDashboard/ui/badge";
import { Button } from "../../Franchise/D2CDashboard/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../Franchise/D2CDashboard/ui/card";
import { EmptyState } from "../../Franchise/D2CDashboard/StateBlock";
import { formatDateTime } from "../common/format";
import { foldEvents, formatTimeRange, groupOf, readShowAll, visibleEvents, writeShowAll } from "./activityFold";
import { actorLabel, actorTone } from "./journeyStages";

const DOT_TONE = {
  info: "tw-bg-blue-50 tw-text-blue-700 tw-ring-blue-200",
  warning: "tw-bg-amber-50 tw-text-amber-700 tw-ring-amber-200",
  success: "tw-bg-emerald-50 tw-text-emerald-700 tw-ring-emerald-200",
  neutral: "tw-bg-slate-100 tw-text-slate-600 tw-ring-slate-200",
};

// Milestones stand out (filled dot, bold), details read normally, internal rows are muted.
const GROUP_STYLE = {
  milestone: { dot: "tw-ring-2", inner: "tw-size-3", label: "tw-font-semibold tw-text-foreground", row: "" },
  detail: { dot: "tw-ring-1", inner: "tw-size-2", label: "tw-font-normal tw-text-foreground", row: "" },
  internal: { dot: "tw-ring-1", inner: "tw-size-1.5", label: "tw-font-normal tw-text-muted-foreground", row: "tw-opacity-70" },
};

const ActorChip = ({ actor }) => (
  <Badge tone={actorTone(actor)} title={actor?.id ? `${actorLabel(actor)} ${actor.id}` : undefined}>
    {actorLabel(actor)}
  </Badge>
);

const FoldedEvents = ({ id, events }) => (
  <ol id={id} aria-label="Folded activity" className="tw-mb-0 tw-mt-2 tw-list-none tw-space-y-1 tw-border-l tw-pl-3">
    {events.map((event, index) => (
      <li key={`${event.at}-${index}`} className="tw-text-xs tw-text-muted-foreground">
        <time dateTime={event.at || undefined}>{formatDateTime(event.at)}</time>
        {event.reason && <span className="tw-ml-2 tw-break-words">Reason: {event.reason}</span>}
      </li>
    ))}
  </ol>
);

const ActivityRow = ({ row, last, open, onToggle }) => {
  const style = GROUP_STYLE[row.group] || GROUP_STYLE.detail;
  const folded = row.count > 1;
  const panelId = `activity-fold-${row.key}`;
  const label = <span className={`tw-text-sm ${style.label}`}>{row.label}</span>;
  return (
    <li data-kind={row.kind} data-group={row.group} className={`tw-relative tw-flex tw-gap-3 tw-pb-4 last:tw-pb-0 ${style.row}`}>
      {!last && <span aria-hidden="true" className="tw-absolute tw-bottom-0 tw-left-[13px] tw-top-7 tw-w-px tw-bg-border" />}
      <span
        aria-hidden="true"
        className={`tw-relative tw-mt-0.5 tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full ${style.dot} ${DOT_TONE[actorTone(row.actor)]}`}
      >
        <span className={`${style.inner} tw-rounded-full tw-bg-current`} />
      </span>
      <div className="tw-min-w-0 tw-flex-1 tw-pt-0.5">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1">
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {folded ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={panelId}
                className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-border-0 tw-bg-transparent tw-p-0 tw-text-left hover:tw-underline"
              >
                {open ? <ChevronDown aria-hidden="true" className="tw-size-3.5" /> : <ChevronRight aria-hidden="true" className="tw-size-3.5" />}
                {label}
                <span className="tw-text-xs tw-font-medium tw-text-muted-foreground">×{row.count}</span>
              </button>
            ) : (
              label
            )}
            <ActorChip actor={row.actor} />
          </span>
          <time dateTime={row.newestAt || undefined} className="tw-text-xs tw-text-muted-foreground">
            {folded ? formatTimeRange(row.oldestAt, row.newestAt) : formatDateTime(row.newestAt)}
          </time>
        </div>
        {!folded && row.events[0].reason && (
          <div className="tw-mt-0.5 tw-break-words tw-text-xs tw-text-muted-foreground">Reason: {row.events[0].reason}</div>
        )}
        {folded && open && <FoldedEvents id={panelId} events={row.events} />}
      </div>
    </li>
  );
};

// The registration's activity, newest first, as the service sends it. Internal rows (receipts,
// download links, ERP bookkeeping) are hidden unless "Show all activity" is on, which this browser
// remembers. A run of the same event folds into one row that opens into its events. Folding is over
// what is loaded: `onLoadOlder` appends the next page and the rows are folded again.
const JourneyActivityLog = ({ events, hasOlder, loadingOlder, olderError, onLoadOlder }) => {
  const [showAll, setShowAll] = useState(readShowAll);
  const [openRows, setOpenRows] = useState(() => new Set());
  const all = useMemo(() => (Array.isArray(events) ? events : []), [events]);
  const hiddenCount = useMemo(() => all.filter((event) => groupOf(event) === "internal").length, [all]);
  const rows = useMemo(() => foldEvents(visibleEvents(all, showAll)), [all, showAll]);

  const toggleShowAll = () => {
    const next = !showAll;
    writeShowAll(next);
    setShowAll(next);
  };
  const toggleRow = (key) =>
    setOpenRows((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <Card>
      <CardHeader className="tw-items-center tw-pb-2">
        <div>
          <CardTitle as="h2">Activity</CardTitle>
          <CardDescription>Newest first. From the registration and its invoice, plans, payments, services and referral.</CardDescription>
        </div>
        <Button variant="outline" size="sm" aria-pressed={showAll} onClick={toggleShowAll}>
          {showAll ? "Hide internal activity" : `Show all activity${hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ""}`}
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState
            icon={History}
            title={all.length === 0 ? "No activity recorded yet" : "Only internal activity loaded so far"}
          />
        ) : (
          <ol aria-label="Warranty activity" className="tw-relative tw-m-0 tw-list-none tw-p-0">
            {rows.map((row, index) => (
              <ActivityRow
                key={row.key}
                row={row}
                last={index === rows.length - 1}
                open={openRows.has(row.key)}
                onToggle={() => toggleRow(row.key)}
              />
            ))}
          </ol>
        )}
        {(hasOlder || olderError) && (
          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
            {olderError && (
              <span role="alert" className="tw-text-xs tw-text-red-700">
                {olderError}
              </span>
            )}
            {hasOlder && (
              <Button variant="outline" size="sm" onClick={onLoadOlder} disabled={loadingOlder}>
                {loadingOlder ? "Loading" : "Load older"}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JourneyActivityLog;
